import React, {useState} from "react";
import {Modal} from "react-bootstrap";
import {GoogleLogin} from "@react-oauth/google";
import {CELULAR_AYUDA, celularEsValido, LoadingBoton} from "../../_helpers";

/**
 * El botón "Entrar con Google", con el paso del celular para el que todavía no tiene cuenta.
 *
 * <h3>Por qué hace falta un paso extra</h3>
 * Google devuelve mail, nombre y apellido, y ningún teléfono. Como el celular pasó a ser obligatorio
 * —es el único canal por el que comprador y vendedor se encuentran fuera del sitio—, esta vía no
 * puede seguir creando cuentas sin él: quedaría una puerta de atrás que esquiva la regla.
 *
 * <h3>Por qué se intenta primero sin preguntar nada</h3>
 * El front no sabe si ese mail ya tiene cuenta hasta que lo consulta, y a quien YA está registrado
 * pedirle el teléfono para entrar sería un paso de más en cada login. Así que primero se prueba
 * derecho: si la cuenta existe, entra y no ve nada; si no existe, el backend corta con un 428 y
 * recién ahí aparece el modal. La credencial de Google se guarda para reenviarla con el número, sin
 * obligar a la persona a pasar otra vez por el popup de Google.
 */
export const GoogleAuthButton = ({login, onSuccess}) => {

    /** La credencial que quedó esperando el celular. Su presencia es lo que abre el modal. */
    const [credencialPendiente, setCredencialPendiente] = useState(undefined);
    const [celular, setCelular] = useState("");
    const [tocado, setTocado] = useState(false);
    const [entrando, setEntrando] = useState(false);

    const entrar = (credencial, numeroCelular) => {
        setEntrando(true);

        login(undefined, undefined, credencial, onSuccess, {
            numeroCelular,
            onError: (error) => {
                setEntrando(false);

                // 428 Precondition Required: la cuenta no existe y falta el teléfono para crearla.
                if (error?.codigo === 428) {
                    setCredencialPendiente(credencial);
                    return true;
                }

                return false;
            },
        });
    };

    const confirmarCelular = () => {
        setTocado(true);
        if (!celularEsValido(celular)) return;

        entrar(credencialPendiente, celular);
    };

    const celularInvalido = tocado && !celularEsValido(celular);

    return (
        <>
            <GoogleLogin
                onSuccess={(credentialResponse) => entrar(credentialResponse.credential, undefined)}
                onError={(err) => console.log("Login Failed", err)}
            />

            <Modal
                show={!!credencialPendiente}
                onHide={() => setCredencialPendiente(undefined)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Falta un dato</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>
                        Estás creando tu cuenta con Google. Necesitamos tu celular: es el número con el
                        que van a contactarte por WhatsApp cuando compres o vendas.
                    </p>

                    <div className="form-group">
                        <label htmlFor="celularGoogle">Celular</label>
                        <input
                            id="celularGoogle"
                            className="form-control"
                            type="tel"
                            inputMode="tel"
                            autoFocus
                            value={celular}
                            placeholder="1155667788"
                            onChange={(e) => setCelular(e.target.value)}
                            onBlur={() => setTocado(true)}
                            onKeyDown={(e) => e.key === "Enter" && confirmarCelular()}
                        />
                        {celularInvalido ? (
                            <div className="help-block text-danger">
                                <i className="fas fa-exclamation-circle"></i> Ingresá un celular válido
                            </div>
                        ) : (
                            <small className="form-hint">{CELULAR_AYUDA}</small>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <button
                        type="button"
                        className="button"
                        data-type="primary"
                        disabled={entrando}
                        onClick={confirmarCelular}
                    >
                        Crear mi cuenta
                        <LoadingBoton grabando={entrando}/>
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
